<template>
  <div class="bank_info">
    <div class="flex flex-col py-2">
      <h5 class="font-semibold">Bank Account</h5>
      <template v-if="isAccountLoading">
        <Loading />
      </template>
      <template v-else>
        <template v-if="getCurrentAccount">
          <template v-if="!getCurrentAccount.verified">
            <p class="py-3">
              <span class="font-semibold">Verify your test deposits:</span> Two small test deposits
              (each less than $1) are initiated at the time you set up your My Payments account.
              They should arrive in your bank account in 1-2 business days via ACH transfer.
            </p>
            <p class="py-3">
              In order to verify your account, please check your bank account and enter the
              deposited amounts below. If you do not see the test deposits, please contact
              <a :mailto="supportEmail" class="text-brand-green hover:underline cursor-pointer">{{
                supportEmail
              }}</a>
              for assistance.
            </p>

            <div
              class="rounded border border-brand-mid-gray flex flex-row md:w-1/2 md:mx-auto my-5"
            >
              <div class="flex flex-col border-r border-mid-gray bg-brand-light-gray px-2">
                <div class="flex items-center py-3">
                  <p>Bank Name</p>
                </div>
                <div class="flex items-center py-3">
                  <p>Account Number</p>
                </div>
              </div>
              <div class="flex flex-col border-l border-mid-gray px-3">
                <div class="flex items-center py-3">
                  <p class="font-semibold">
                    {{ getCurrentAccount.stripe_account.bank_name }}
                  </p>
                </div>
                <div class="flex items-center py-3">
                  <p class="font-semibold">******{{ getCurrentAccount.stripe_account.last4 }}</p>
                </div>
              </div>
            </div>

            <h5 class="py-3 font-semibold">Verify Test Deposit Amounts</h5>

            <div class="flex flex-col">
              <div class="flex flex-row py-2">
                <div class="flex items-center mr-4">
                  <p>Test Deposit Amount 1</p>
                </div>
                <div class="flex items-center">
                  <div class="flex flex-row items-center justify-end">
                    <div
                      class="border border-brand-mid-gray bg-brand-light-gray rounded-tl rounded-bl py-2 pl-5 pr-3"
                    >
                      <p class="">$0.</p>
                    </div>
                    <div class="md:w-20">
                      <input
                        type="number"
                        min="0"
                        max="99"
                        step="1"
                        class="w-full px-4 text-sm md:text-base rounded-tr rounded-br border border-brand-mid-gray border-l-0 placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green"
                        placeholder="00"
                        v-model="amount1"
                        @change="validateAmount($event, 'amount1')"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex flex-row py-2">
                <div class="flex items-center mr-3">
                  <p>Test Deposit Amount 2</p>
                </div>
                <div class="flex items-center">
                  <div class="flex flex-row items-center justify-end">
                    <div
                      class="border border-brand-mid-gray bg-brand-light-gray rounded-tl rounded-bl py-2 pl-5 pr-3"
                    >
                      <p class="">$0.</p>
                    </div>
                    <div class="md:w-20">
                      <input
                        type="number"
                        min="0"
                        max="99"
                        step="1"
                        class="w-full px-4 text-sm md:text-base rounded-tr rounded-br border border-brand-mid-gray border-l-0 placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green"
                        placeholder="00"
                        v-model="amount2"
                        @change="validateAmount($event, 'amount2')"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="md:w-1/3 py-5 md:mx-auto">
              <PrimaryButton
                @click="verifyTestAmount"
                :disabled="isAccountLoading"
                :is-loading="isAccountLoading"
                >Verify Test Amounts</PrimaryButton
              >
            </div>
          </template>

          <template v-else>
            <p class="py-3">Your account is verified for electronic bank transfers</p>

            <div
              class="rounded border border-brand-mid-gray flex flex-row md:w-1/2 md:mx-auto my-5"
            >
              <div class="flex flex-col border-r border-mid-gray bg-brand-light-gray px-2">
                <div class="flex items-center py-3">
                  <p>Account Type</p>
                </div>
                <div class="flex items-center py-3">
                  <p>Bank Name</p>
                </div>
                <div class="flex items-center py-3">
                  <p>Account Number</p>
                </div>
                <div class="flex items-center py-3">
                  <p>Date Verified</p>
                </div>
              </div>
              <div class="flex flex-col border-l border-mid-gray px-3">
                <div class="flex items-center py-3">
                  <p class="font-semibold capitalize">
                    {{ getCurrentAccount.stripe_account.account_info.business_type }}
                  </p>
                </div>
                <div class="flex items-center py-3">
                  <p class="font-semibold">
                    {{ getCurrentAccount.stripe_account.bank_name }}
                  </p>
                </div>
                <div class="flex items-center py-3">
                  <p class="font-semibold">******{{ getCurrentAccount.stripe_account.last4 }}</p>
                </div>
                <div class="flex items-center py-3">
                  <p class="font-semibold">
                    {{ getCurrentAccount.stripe_account.verified_time }}
                  </p>
                </div>
              </div>
            </div>

            <div class="md:w-1/3 py-5 md:mx-auto">
              <PrimaryButton @click="editBankDetails">Edit Bank Details</PrimaryButton>
              <p class="py-1 text-xs text-brand-mid-gray italic">Editing bank details cost $5.00</p>
            </div>
          </template>
        </template>
      </template>
    </div>
  </div>
  <ActionToast ref="bankAccountToast" />
</template>
<script>
import { mapGetters } from "vuex"
import { ACCOUNT_GET, ACCOUNT_VERIFY_BANK_ACCOUNT } from "@/store/types"

export default {
  computed: {
    ...mapGetters(["getCurrentAccount", "isAccountLoading"])
  },
  data() {
    return {
      amount1: 0,
      amount2: 0
    }
  },
  mounted() {
    if (!this.getCurrentAccount) {
      this.$store.dispatch(ACCOUNT_GET)
    }
  },
  methods: {
    async verifyTestAmount() {
      const { amount1, amount2 } = this

      if (!amount1 || !amount2) {
        this.$refs.bankAccountToast.show({
          type: "danger",
          hasClose: true,
          content: "Please enter the test deposit amounts."
        })
        return
      }
      try {
        await this.$store.dispatch(ACCOUNT_VERIFY_BANK_ACCOUNT, {
          amount1,
          amount2
        })
        this.$refs.bankAccountToast.show({
          type: "success",
          hasClose: true,
          content: "Account verified."
        })
      } catch (err) {
        this.$refs.bankAccountToast.show({
          type: "danger",
          hasClose: true,
          content: "Incorrect test deposit amounts. Please check your bank account again."
        })
      }
    },
    validateAmount(ev, key) {
      const amount = parseFloat(ev.target.value)
      if (amount > 100) {
        this[key] = 0
      } else {
        this[key] = amount
      }
    },
    editBankDetails() {
      this.$router.push({
        name: "dashboard_my_payments_signup_bank_account_info",
        query: {
          mode: "edit"
        }
      })
    }
  }
}
</script>
